import { Link } from 'wouter';
import type { PatientSummary, SortField, SortOrder } from '../../types/admin';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  ChevronUp,
  ChevronDown,
  MessageSquare,
  Calendar,
  User,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

interface PatientDataTableProps {
  patients: PatientSummary[];
  sortBy: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  onDelete?: (patientId: string) => void;
  isLoading?: boolean;
}

function SortButton({
  field,
  currentSort,
  currentOrder,
  onSort,
  children,
}: {
  field: SortField;
  currentSort: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
}) {
  const isActive = field === currentSort;

  return (
    <button
      onClick={() => onSort(field)}
      className="flex items-center space-x-1 hover:text-gray-900 transition-colors"
    >
      <span>{children}</span>
      {isActive && (
        currentOrder === 'asc' ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )
      )}
    </button>
  );
}

function formatDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

function getPatientDisplayName(patient: PatientSummary): string {
  if (patient.patientFirstName || patient.patientLastName) {
    return `${patient.patientFirstName || ''} ${patient.patientLastName || ''}`.trim();
  }
  // Return truncated patient ID
  return patient.patientId.substring(0, 12) + '...';
}

export default function PatientDataTable({
  patients,
  sortBy,
  sortOrder,
  onSort,
  onDelete,
  isLoading,
}: PatientDataTableProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Loading patients...</div>
        </div>
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <User className="h-12 w-12 mb-4 text-gray-300" />
          <p>No patients found</p>
          <p className="text-sm">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="font-semibold text-gray-700">
              <SortButton
                field="name"
                currentSort={sortBy}
                currentOrder={sortOrder}
                onSort={onSort}
              >
                Patient
              </SortButton>
            </TableHead>
            <TableHead className="font-semibold text-gray-700">
              <SortButton
                field="lastChatTime"
                currentSort={sortBy}
                currentOrder={sortOrder}
                onSort={onSort}
              >
                Last Chat
              </SortButton>
            </TableHead>
            <TableHead className="font-semibold text-gray-700">
              <SortButton
                field="age"
                currentSort={sortBy}
                currentOrder={sortOrder}
                onSort={onSort}
              >
                Age
              </SortButton>
            </TableHead>
            <TableHead className="font-semibold text-gray-700">
              <SortButton
                field="sessionCount"
                currentSort={sortBy}
                currentOrder={sortOrder}
                onSort={onSort}
              >
                Sessions
              </SortButton>
            </TableHead>
            <TableHead className="font-semibold text-gray-700">
              <SortButton
                field="totalMessages"
                currentSort={sortBy}
                currentOrder={sortOrder}
                onSort={onSort}
              >
                Messages
              </SortButton>
            </TableHead>
            <TableHead className="font-semibold text-gray-700 text-right">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {patients.map((patient) => (
            <TableRow
              key={patient.patientId}
              className="hover:bg-gray-50 transition-colors"
            >
              <TableCell>
                <div className="flex items-center space-x-3">
                  <div className="rounded-full bg-blue-100 p-2">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {getPatientDisplayName(patient)}
                    </div>
                    {patient.patientDateOfBirth && (
                      <div className="text-xs text-gray-500">
                        DOB: {patient.patientDateOfBirth}
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">{formatDate(patient.lastChatTime)}</span>
                </div>
              </TableCell>
              <TableCell>
                <span className="text-gray-600">
                  {patient.age !== undefined ? `${patient.age} yrs` : '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {patient.sessionCount}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-1 text-gray-600">
                  <MessageSquare className="h-4 w-4 text-gray-400" />
                  <span>{patient.totalMessages}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex flex-col items-end gap-1">
                  {patient.sessions.map((session) => (
                    <Link
                      key={session.sessionId}
                      href={`/admin/conversation/${encodeURIComponent(patient.patientId)}/${encodeURIComponent(session.sessionId)}`}
                    >
                      <Button variant="outline" size="sm" className="max-w-[260px]">
                        <MessageSquare className="h-3.5 w-3.5 mr-2 shrink-0" />
                        <span className="truncate">
                          {session.title || formatDate(session.startTime)}
                        </span>
                      </Button>
                    </Link>
                  ))}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onDelete(patient.patientId)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete history
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
